import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';
import * as Sentry from '@sentry/browser';

import { tokens, TextInput, Select, Tooltip, Icon } from '@hotjar/hotsauce';
import {
  PaymentIcon,
  SUPPORTED_CARDS_ICONS,
} from '@hotjar/webapp/components/PaymentIcons/PaymentIcons';
import { useOwnedAccount } from '@hotjar/account';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { PaymentMethod } from '@hotjar/webapp/billing/payment';
import {
  checkHostedFormValidity,
  setFocus,
  setupHostedFields,
  tokenizeCard,
} from '@hotjar/checkout/src/braintree/hosted-fields';
import { useCountries } from '@hotjar/webapp/hooks/useCountries';
import type { BillingCycleFormField } from '@hotjar/checkout/src/PaymentDetails/components/BillingCycleSelector';
import {
  PAYMENT_FORM_SUBMITTED_ERRORS,
  trackPaymentFormSubmitted,
} from '@hotjar/checkout/src/utils/tracking';

import type { AgreeNoticeFormField } from '../AgreeNotice/AgreeNotice';
import { S } from './CreditCardForm.styles';

export const CC_FORM_ID = 'credit-card-fields';
const ccNumberId = `${CC_FORM_ID}-number`;
const cvvFieldId = `${CC_FORM_ID}-cvv`;
const expirationMonthFieldId = `${CC_FORM_ID}-expiration-month`;
const expirationYearFieldId = `${CC_FORM_ID}-expiration-year`;
const nameOnCardFieldId = `${CC_FORM_ID}-name-on-card`;
const billingCountryFieldId = `${CC_FORM_ID}-billing-country`;
const cityFieldId = `${CC_FORM_ID}-city`;
const postalCodeFieldId = `${CC_FORM_ID}-postal-code`;

function HostedField({
  onClick,
  children,
  isInvalid,
  minimizeIconSpace = false,
}: {
  onClick: () => void;
  children: React.ReactElement;
  isInvalid: boolean;
  minimizeIconSpace?: boolean;
}) {
  return (
    <S.InputContainer
      onClick={onClick}
      isInvalid={isInvalid}
      isReadonly={false}
      isActionPresent={false}
      isDisabled={false}
    >
      <S.Input isInvalid={isInvalid} readOnly={false} isIconPresent={isInvalid} inputSize="m">
        {children}
      </S.Input>
      {isInvalid && (
        <S.InputIcon
          icon="warning_alt"
          size="m"
          color="text_danger"
          minimizeIconSpace={minimizeIconSpace}
        />
      )}
    </S.InputContainer>
  );
}

interface FormInputs {
  cardholderName: string;
  city: string;
  country: string;
  postalCode: string;
}

interface Props {
  visible: boolean;
  setErrorMessage(message: string): void;
  setIsLoading(isLoading: boolean): void;
  onSubmit(): void;
  onSuccess(paymentMethod: PaymentMethod): void;
}

export function CreditCardForm({
  visible,
  setErrorMessage,
  onSubmit,
  onSuccess,
  setIsLoading,
}: Props): React.ReactElement | null {
  const { t } = useTranslationWithPrefix('billing:payment-details');
  const [hostedFieldsErrorList, setHostedFieldsErrorList] = useState<string[]>([]);
  const { data: ownedAccount } = useOwnedAccount();
  const { unsanctionedCountries } = useCountries();

  useEffect(() => {
    setupHostedFields({
      accountId: ownedAccount?.id,
      formId: CC_FORM_ID,
      onValidationError: (errors) => {
        // technically we could report on errors on change, but that is not how the form currently works so
        // for now we only set the state of the list to be used by hostedFieldsErrors once the form is submitted
        setHostedFieldsErrorList(errors);
      },
    });
  }, [ownedAccount]);

  const {
    register,
    formState: { errors, isSubmitted },
    handleSubmit,
  } = useFormContext<FormInputs>();

  const hostedFieldsErrors = isSubmitted
    ? {
        number: hostedFieldsErrorList.includes('number') ? t('form:error-card-number') : undefined,
        cvv: hostedFieldsErrorList.includes('cvv') ? t('form:error-cvc') : undefined,
        expirationDate:
          hostedFieldsErrorList.includes('expirationYear') ||
          hostedFieldsErrorList.includes('expirationMonth')
            ? t('form:error-expiration-date')
            : undefined,
      }
    : {};

  async function handleInvalidSubmit(
    invalidFields: FieldErrors<FormInputs & AgreeNoticeFormField & BillingCycleFormField>,
  ) {
    const { agreeNotice, billingCycle, ...billingDetails } = invalidFields;
    const { isValid, invalidFields: invalidHostedFields } = checkHostedFormValidity();

    if (agreeNotice) {
      trackPaymentFormSubmitted(ownedAccount, false, PAYMENT_FORM_SUBMITTED_ERRORS.agreeNotice);
    }

    if (billingCycle) {
      trackPaymentFormSubmitted(ownedAccount, false, PAYMENT_FORM_SUBMITTED_ERRORS.billingCycle);
    }

    if (Object.keys(billingDetails).length) {
      trackPaymentFormSubmitted(ownedAccount, false, PAYMENT_FORM_SUBMITTED_ERRORS.billingDetails);
    }

    if (!isValid) {
      trackPaymentFormSubmitted(ownedAccount, false, PAYMENT_FORM_SUBMITTED_ERRORS.hostedForm);
    }
    setHostedFieldsErrorList(invalidHostedFields);
  }

  async function handleValidSubmit(data: FormInputs) {
    // Braintree fields are handled outside of react-hook-form, so we need to check
    //  the validity of that part of the form separately
    const { isValid } = checkHostedFormValidity();
    if (!isValid) {
      handleInvalidSubmit({});
      return;
    }
    setErrorMessage('');
    setIsLoading(true);

    try {
      const paymentMethod = (await tokenizeCard({
        cardholderName: data.cardholderName,
        city: data.city,
        postalCode: data.postalCode,
        countryCode: data.country,
      })) as PaymentMethod;
      onSuccess(paymentMethod);
    } catch (error) {
      // We're handling all FE validations before this step, so only BE errors can happen here

      // Braintree Server tokenization errors
      //  possible errors: https://braintree.github.io/braintree-web/current/HostedFields.html#tokenize-examples
      Sentry.captureException(error);
      setErrorMessage(t('form:error-generic'));
      setIsLoading(false);
    }
  }

  return (
    // We're hiding this using CSS (instead of not rendering at all), so
    //  Braintree's buttons are not destroyed when toggling between payment methods
    <S.Container style={{ display: visible ? 'block' : 'none' }}>
      <S.CardIcons>
        {SUPPORTED_CARDS_ICONS.map((payment) => (
          <PaymentIcon key={payment} name={payment} alt={payment} />
        ))}
      </S.CardIcons>
      <div>
        <S.Form
          id={CC_FORM_ID}
          onSubmit={(event) => {
            onSubmit();
            handleSubmit(handleValidSubmit, handleInvalidSubmit)(event);
          }}
        >
          <S.Row>
            <S.Field
              inputId={ccNumberId}
              labelText={t('form:card-number')}
              isRequired
              width="50%"
              isInvalid={!!hostedFieldsErrors.number}
              invalidText={hostedFieldsErrors.number}
            >
              <HostedField
                onClick={() => setFocus('number')}
                isInvalid={!!hostedFieldsErrors.number}
              >
                <S.HostedFieldInnerContainer id={ccNumberId} />
              </HostedField>
            </S.Field>
            <S.Field
              inputId={expirationYearFieldId}
              labelText={t('form:expiration-date')}
              isRequired
              width="25%"
              isInvalid={!!hostedFieldsErrors.expirationDate}
              invalidText={hostedFieldsErrors.expirationDate}
            >
              <HostedField
                onClick={() => setFocus('expirationMonth')}
                isInvalid={!!hostedFieldsErrors.expirationDate}
                minimizeIconSpace
              >
                <S.DateInnerContainer>
                  <div id={expirationMonthFieldId} />
                  <div id={expirationYearFieldId} />
                </S.DateInnerContainer>
              </HostedField>
            </S.Field>
            <S.Field
              inputId={cvvFieldId}
              labelText={t('form:cvc')}
              isRequired
              width="25%"
              isInvalid={!!hostedFieldsErrors.cvv}
              invalidText={hostedFieldsErrors.cvv}
            >
              <HostedField onClick={() => setFocus('cvv')} isInvalid={!!hostedFieldsErrors.cvv}>
                <S.HostedFieldInnerContainer id={cvvFieldId} />
              </HostedField>
              <S.TooltipContainer>
                <Tooltip text={t('form:cvc-tooltip')}>
                  <Icon icon="info" />
                </Tooltip>
              </S.TooltipContainer>
            </S.Field>
          </S.Row>
          <S.Row>
            <S.Field
              inputId={nameOnCardFieldId}
              labelText={t('form:name-on-card')}
              width={`calc(75% - ${tokens.spacer.x1})`}
              isRequired
              isInvalid={!!errors.cardholderName}
              invalidText={errors.cardholderName?.message}
            >
              <TextInput
                id={nameOnCardFieldId}
                placeholder={t('form:placeholder-name')}
                ref={register({ required: t<string>('form:error-required') })}
                name="cardholderName"
                isInvalid={!!errors.cardholderName}
              />
            </S.Field>
          </S.Row>
          <S.Row>
            <S.Field
              inputId={billingCountryFieldId}
              labelText={t('form:billing-country')}
              width="37.5%"
              isInvalid={!!errors.country}
              invalidText={errors.country?.message}
              isRequired
            >
              <Select
                id={billingCountryFieldId}
                ref={register({ required: t<string>('form:error-required') })}
                name="country"
                isInvalid={!!errors.country}
              >
                <option value="">{t('form:placeholder-country')}</option>
                {unsanctionedCountries.map(({ label, value }) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </S.Field>
            <S.Field
              inputId={cityFieldId}
              labelText={t('form:city')}
              isRequired
              width="37.5%"
              isInvalid={!!errors.city}
              invalidText={errors.city?.message}
            >
              <TextInput
                id={cityFieldId}
                ref={register({ required: t<string>('form:error-required') })}
                isInvalid={!!errors.city}
                name="city"
              />
            </S.Field>
            <S.Field
              inputId={postalCodeFieldId}
              labelText={t('form:postal-code')}
              isRequired
              width="25%"
              isInvalid={!!errors.postalCode}
              invalidText={errors.postalCode?.message}
            >
              <TextInput
                id={postalCodeFieldId}
                name="postalCode"
                isInvalid={!!errors.postalCode}
                ref={register({ required: t<string>('form:error-required') })}
              />
            </S.Field>
          </S.Row>
        </S.Form>
      </div>
    </S.Container>
  );
}
